import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { themeGet } from '@styled-system/theme-get';
import styled from 'styled-components';

import { repickWinner, deleteWinner } from '../data/data';
import Text from '../../../../../shared/Text/Text';
import Card from '../../../../../shared/Card/Card.jsx';
import {
  Table,
  ShadedTr,
} from '../../../../../shared/TableComponents/StyledComponents';

import TableHead from './TableHead.jsx';

const WinnersTable = ({ prize, sortingStatus, changeSort, winnerPicked }) => {
  const { t } = useTranslation('campaigns');
  const queryClient = useQueryClient();
  const winnerTableText = winnerPicked
    ? t('prizes.pickWinnersModal.noWinners')
    : t('prizes.pickWinnersModal.configureWinners');

  const mutationSideEffects = {
    onSuccess: () => {
      queryClient.invalidateQueries(['getPrize']);
      queryClient.invalidateQueries(['campaignData', prize.campaignUuid]);
    },
  };

  const { mutate: repickWinnerMutation } = useMutation(
    repickWinner,
    mutationSideEffects,
  );

  const { mutate: deleteWinnerMutation } = useMutation(
    deleteWinner,
    mutationSideEffects,
  );

  return (
    <RightSideSection>
      <CardWinners title={t('prizes.pickWinnersModal.tableTitle')}>
        {prize.winners.length ? (
          <TableSection>
            <Table>
              <colgroup>
                <col span="1" style={{ width: '30%' }}></col>
                <col span="1" style={{ width: '15%' }}></col>
                <col span="1" style={{ width: '25%' }}></col>
                <col span="1" style={{ width: '30%' }}></col>
              </colgroup>
              <TableHead
                sortingStatus={sortingStatus}
                changeSort={changeSort}
              />
              <tbody>
                {prize.winners.map(row => {
                  return (
                    <ShadedTr key={row.uuid}>
                      <Td>{row.name}</Td>
                      <TdRight>{row.tickets}</TdRight>
                      <Td>
                        <Email href={`mailto:${row.email}`}>{row.email}</Email>
                      </Td>
                      <ActionTd>
                        <ActionBtn
                          onClick={() =>
                            repickWinnerMutation({ prize, userId: row.uuid })
                          }
                        >
                          {t('prizes.pickWinnersModal.repick')}
                        </ActionBtn>
                        <ActionBtn
                          onClick={() =>
                            deleteWinnerMutation({ prize, userId: row.uuid })
                          }
                        >
                          {t('prizes.pickWinnersModal.delete')}
                        </ActionBtn>
                      </ActionTd>
                    </ShadedTr>
                  );
                })}
              </tbody>
            </Table>
          </TableSection>
        ) : (
          <TextBox>
            <Text as="span" variant="small2">
              {winnerTableText}
            </Text>
          </TextBox>
        )}
      </CardWinners>
    </RightSideSection>
  );
};

export default WinnersTable;

const TextBox = styled.div`
  margin: 9px 24px 24px;
`;

const RightSideSection = styled.div`
  width: 65%;
  @media (max-width: 900px) {
    width: 100%;
  }
`;

const TableSection = styled.div`
  padding: 9px 0px 5px;
`;

const Td = styled.td`
  padding: 0 15px 0 0;
  border-top: 1px solid ${themeGet('colors.black.10')};
  border-bottom: none !important;
  color: ${themeGet('colors.black.100')};
  font-size: 12px;
  padding-right: 24px;
  &:first-child {
    padding: 0px 24px;
  }
`;

const TdRight = styled(Td)`
  padding-right: 60px;
  text-align: right;
`;

const ActionTd = styled.td`
  border-top: 1px solid ${themeGet('colors.black.10')};
  text-align: right;
  border-bottom: none !important;
`;

const Email = styled.a`
  font-weight: 400;
  font-size: 12px;
  color: ${themeGet('colors.primary.100')};
`;

const ActionBtn = styled.a`
  padding: 8px 0 8px 24px;
  color: ${themeGet('colors.primary.100')};
  font-weight: 400;
  font-size: 12px;

  :last-child {
    padding-right: 30px;
`;

const CardWinners = styled(Card)`
  margin-top: 0px;
  margin-right: 0px;
  @media (max-width: 900px) {
    margin: 24px 0px;
  }
`;
